import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useSport } from "../contexts/SportContext";
import { getEffectiveTier } from "../utils/features";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function dismissedRecently(storageKey) {
  try {
    const ts = localStorage.getItem(storageKey);
    if (!ts) return false;
    return Date.now() - Number(ts) < SEVEN_DAYS_MS;
  } catch {
    return false;
  }
}

/**
 * Cricket Pro activation cue. Renders only when sport === "cricket" AND the user
 * is on the free tier. Dismissible — hidden for 7 days after dismissal.
 *
 * variant="banner": full promo card (Leaderboard page).
 * variant="nudge": one-line inline nudge (Match Day builder, below lock timer).
 */
export default function CricketProBanner({ variant = "banner", storageKey }) {
  const { dbUser } = useAuth();
  const { sport } = useSport();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => dismissedRecently(storageKey));

  const isFreeCricket = sport === "cricket" && getEffectiveTier(dbUser) === "free";
  if (!isFreeCricket || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch {}
    setDismissed(true);
  };

  if (variant === "nudge") {
    return (
      <div className="flex items-center justify-between gap-2 mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="cricket-pro-nudge">
        <p className="text-xs text-amber-200">
          Pro users get 25 minutes after lock.{" "}
          <button onClick={() => navigate("/subscribe")} className="font-bold text-amber-400 underline underline-offset-2" data-testid="cricket-pro-nudge-cta">
            Upgrade →
          </button>
        </p>
        <button onClick={dismiss} className="text-amber-300/70 hover:text-amber-200" data-testid="cricket-pro-nudge-dismiss" aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-slate-800/40 p-4" data-testid="cricket-pro-banner">
      <button onClick={dismiss} className="absolute top-2 right-2 text-slate-400 hover:text-white" data-testid="cricket-pro-banner-dismiss" aria-label="Dismiss">
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-amber-500/20">
          <Crown size={18} className="text-amber-400" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-white">Get more from APL</h3>
          <p className="mt-1 text-xs text-slate-300">
            Extended Match Day lock, unlimited predictor questions, ad-free, extra budget. Try APL Pro.
          </p>
          <button
            onClick={() => navigate("/subscribe")}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-xs font-bold text-black hover:bg-amber-400 transition-colors"
            data-testid="cricket-pro-banner-cta"
          >
            <Crown size={14} /> Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
}
